import { BroadcastManyQuery, EnumBroadcastResourcesType } from "gql/generated";
import { Flex } from "styled-system/jsx";
import { HeaderImage } from "./HeaderImage";
import { Button, Icon, Tag, Text } from "@ttc3k/trekker";
import { CheckCircleSolid, Clock, XmarkCircle } from "iconoir-react";
import { DashedCircle } from "shared/components/atoms";
import { getMultilingualString } from "shared/utils";
import { BroadcastStatus } from "apps/home/constants";
import { BroadcastStatusIndicator } from "shared/components";
import { useTranslation } from "react-i18next";

type ViewResourceProps = {
  resource: NonNullable<BroadcastManyQuery["broadcastMany"][0]["resources"][0]>;
  onResourceClick: (
    resource: NonNullable<
      BroadcastManyQuery["broadcastMany"][0]["resources"][0]
    >,
  ) => void;
  status: BroadcastStatus;
  allowSaveAndEdit?: boolean;
  formProgress?: { answered: number; total: number } | null;
  submittedCount?: number;
  pendingCount?: number;
  draftCount?: number;
  rejectedCount?: number;
  bookedCount?: number;
};

function getFormCta(
  status: BroadcastStatus,
  allowSaveAndEdit: boolean,
): string | null {
  switch (status) {
    case "FORM_DRAFT":
      return "FORMS_FORM_DRAFT";
    case "FORM_SUBMITTED":
      return allowSaveAndEdit ? "FORMS_FORM_SUBMITTED" : "FORMS_FORM_SUBMITTED_LOCKED";
    case "FORM_PENDING_REVIEW":
      return "FORMS_FORM_PENDING_REVIEW";
    case "FORM_REJECTED":
      return "FORMS_FORM_REJECTED";
    default:
      return null;
  }
}

export const ResourceCard = ({
  resource,
  onResourceClick,
  status,
  allowSaveAndEdit = false,
  formProgress,
  submittedCount,
  pendingCount,
  draftCount,
  rejectedCount,
  bookedCount,
}: ViewResourceProps) => {
  const { t } = useTranslation("home", { keyPrefix: "VIEWS.BROADCAST" });

  const isForm = resource.type === EnumBroadcastResourcesType.Forms;
  const isCourse = resource.type === EnumBroadcastResourcesType.Course;
  const isStorefront = resource.type === EnumBroadcastResourcesType.Storefront;
  const isMultiSubmission = submittedCount !== undefined;

  let ctaKey: string;
  if (isForm) {
    const formCtaKey = getFormCta(status, allowSaveAndEdit);
    ctaKey = `STATUS_RESOURCE_TYPE_CTA.${formCtaKey ?? "FORMS"}`;
  } else if (isCourse) {
    ctaKey = `STATUS_RESOURCE_TYPE_CTA.${resource.type}_${status}`;
  } else {
    ctaKey = `STATUS_RESOURCE_TYPE_CTA.${resource.type}`;
  }

  return (
    <Flex
      bg={"white"}
      borderRadius={"150"}
      p={"250"}
      shadow={"100"}
      gap={"300"}
      flexDir={"column"}
    >
      <Flex flexDir={"column"} gap={"200"}>
        <HeaderImage
          type={"DEFAULT"}
          image={resource.coverImage}
          size={"sm"}
          doNotShowFallback
          smartFill
        />

        <Flex flexDir={"column"} gap={"50"}>
          <Text visual={"bodySemiBold"} color={"text.dark"} lineClamp={2}>
            {getMultilingualString(resource.title)}
          </Text>
          {resource.description && (
            <Text visual={"smallRegular"} color={"text.mid"} lineClamp={2}>
              {getMultilingualString(resource.description)}
            </Text>
          )}
          {isForm && formProgress && status === "FORM_DRAFT" && (
            <Text visual={"smallRegular"} color={"text.mid"}>
              {t("FORM_PROGRESS", {
                answered: formProgress.answered,
                total: formProgress.total,
              })}
            </Text>
          )}
        </Flex>
      </Flex>

      <Flex
        width={"full"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Flex alignItems={"center"} gap={"100"} flexWrap="wrap">
          <Tag visual={"primary"} mode={"light"}>
            {t(`RESOURCE_TYPE.${resource.type}`)}
          </Tag>

          {isMultiSubmission ? (
            <Flex alignItems="center" gap="150">
              {(submittedCount ?? 0) > 0 && (
                <Flex alignItems="center" gap="75">
                  <Text visual="smallSemiBold" color="text.success.mid">
                    {t("FORM_MULTI_SUBMITTED_COUNT", { count: submittedCount })}
                  </Text>
                  <Icon Element={CheckCircleSolid} color="icon.success.mid" size="sm" />
                </Flex>
              )}
              {(pendingCount ?? 0) > 0 && (
                <Flex alignItems="center" gap="75">
                  <Text visual="smallSemiBold" color="text.warning.mid">
                    {t("FORM_MULTI_PENDING_COUNT", { count: pendingCount })}
                  </Text>
                  <Icon Element={Clock} color="icon.warning.mid" size="sm" />
                </Flex>
              )}
              {(rejectedCount ?? 0) > 0 && (
                <Flex alignItems="center" gap="75">
                  <Text visual="smallSemiBold" color="text.error.mid">
                    {t("FORM_MULTI_REJECTED_COUNT", { count: rejectedCount })}
                  </Text>
                  <Icon Element={XmarkCircle} color="icon.error.mid" size="sm" />
                </Flex>
              )}
              {(draftCount ?? 0) > 0 && (
                <Flex alignItems="center" gap="75">
                  <Text visual="smallSemiBold" color="text.highlight.mid">
                    {t("FORM_MULTI_DRAFT_COUNT", { count: draftCount })}
                  </Text>
                  <DashedCircle color="icon.highlight.light" width="16px" height="16px" />
                </Flex>
              )}
              {(submittedCount ?? 0) === 0 && (pendingCount ?? 0) === 0 && (rejectedCount ?? 0) === 0 && (draftCount ?? 0) === 0 && (
                <BroadcastStatusIndicator status={status} />
              )}
            </Flex>
          ) : isStorefront && bookedCount !== undefined ? (
            <Flex alignItems="center" gap="150">
              {bookedCount > 0 ? (
                <Flex alignItems="center" gap="75">
                  <Text visual="smallSemiBold" color="text.success.mid">
                    {t("STOREFRONT_BOOKING_COUNT", { count: bookedCount })}
                  </Text>
                  <Icon Element={CheckCircleSolid} color="icon.success.mid" size="sm" />
                </Flex>
              ) : (
                <BroadcastStatusIndicator status={status} />
              )}
            </Flex>
          ) : (
            <BroadcastStatusIndicator status={status} />
          )}
        </Flex>

        <Button
          onClick={() => onResourceClick(resource)}
          type="button"
          mode={"light"}
          size={"sm"}
        >
          {t(ctaKey)}
        </Button>
      </Flex>
    </Flex>
  );
};
